/**
 * Typed wrappers around the Kai-API dashboard routes.
 */

import { apiGet } from './client';

/* Common filters
 * -------------------------------------------------------------------------- */

export type DateRangeKey =
  | 'all'
  | 'today'
  | '7_days'
  | '30_days'
  | '60_days'
  | '90_days'
  | '180_days'
  | 'current_quarter'
  | 'last_quarter'
  | 'ytd'
  /** All Time, capped at a 2-year window for query safety. */
  | '2_years'
  | 'all_time';

export interface DashboardFilters {
  dateRange?: DateRangeKey;
  startDate?: string;
  endDate?: string;
  provider?: string;
  hospital?: string;
}

const filtersToQuery = (filters: DashboardFilters = {}) => ({
  dateRange: filters.dateRange,
  startDate: filters.startDate,
  endDate: filters.endDate,
  provider: filters.provider,
  hospital: filters.hospital,
});

/* /category-scores
 * -------------------------------------------------------------------------- */

export interface CategoryScore {
  id: string;
  category: string;
  fullName: string;
  score: number;
  trend: 'up' | 'down' | 'neutral';
  change: number;
  totalCharts: number;
  previousScore?: number | null;
  /**
   * Legacy rank from the pre-aggregated weekly/rolling tables. The Home
   * RSQ tile no longer reads this — it uses GET /rank/:domain (see
   * DomainRank), which computes rank over the active window with the same
   * formula as the score. Kept for back-compat with other consumers.
   */
  rank?: number | null;
  /** Size of the peer cohort (denominator for `rank`). Null when unavailable. */
  cohortSize?: number | null;
  /**
   * 'strict'   -> the score is for the requested date window.
   * 'all_time' -> the requested window had zero rows, the API widened to all-time
   *               so the hero still shows a number. Client renders a caveat.
   */
  windowFallback?: 'strict' | 'all_time';
}

export const getCategoryScores = (filters: DashboardFilters = {}, signal?: AbortSignal) =>
  apiGet<CategoryScore[]>('/category-scores', { query: filtersToQuery(filters), signal });

/* /category-trends
 * -------------------------------------------------------------------------- */

export interface CategoryTrendPoint {
  date: string;
  rsq: number;
}

export const getCategoryTrends = (filters: DashboardFilters = {}, signal?: AbortSignal) =>
  apiGet<CategoryTrendPoint[]>('/category-trends', { query: filtersToQuery(filters), signal });

/* /rank/:domain
 * -------------------------------------------------------------------------- */

export interface DomainRank {
  /** Domain key the rank applies to ('rsq' | 'ffs' | 'vbc'). */
  domain: string;
  /**
   * Peer rank (1 = best) over the ACTIVE filter window. The API computes
   * every provider's score with the same formula as the domain score and
   * ranks this NPI within that list, so rank follows whatever period is
   * set. Null when the domain is unscored (ffs/vbc today), no NPI anchor
   * is available, or the anchor has no charts in the window.
   */
  rank: number | null;
  /** Providers scored in the cohort (denominator for `rank`). Null when rank is null. */
  peerCount: number | null;
  /** The anchor provider's own windowed score (0-100) the rank was computed on. */
  score: number | null;
}

export const getDomainRank = (
  domain: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<DomainRank>(`/rank/${encodeURIComponent(domain)}`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /chief-complaints
 * -------------------------------------------------------------------------- */

export interface ChiefComplaint {
  id: string;
  name: string;
  count: number;
  /** Aggregated RSQ score for this complaint over the window. Null when denom = 0. */
  score?: number | null;
  /** Target percent shown as a tick on the bar. Sourced from the org-set Vida goal; falls back to 50 when none is set. */
  goal?: number | null;
  /** Where `goal` came from: 'vida' = organization-set target, 'default' = fallback (no goal set yet). */
  goalSource?: 'vida' | 'default' | null;
  /**
   * Period-over-period direction: this window's row score against the prior
   * window of equal length (same formula, +/-2pt threshold), the same
   * comparison the Home tile and Vida make. Falls back to the rolling view
   * only for open-ended windows. `neutral` when there is no prior score.
   */
  trend?: 'up' | 'down' | 'neutral';
  /** Signed RSQ delta vs the prior window; null when insufficient data. */
  trendDelta?: number | null;
  /** The prior window's row score the delta was taken against. */
  previousScore?: number | null;
  /**
   * Monthly RSQ sparkline series, oldest-first. Same source/window/math as the
   * complaint Trends panel (SUM/SUM per month over the active filter), so the
   * row sparkline and the Trends bars agree. Empty when unavailable.
   */
  history?: number[];
  /** Tenant-wide peer rank for this complaint; null when unavailable. */
  rank?: number | null;
  /** Cohort size the rank is out of; null when unavailable. */
  peerCount?: number | null;
}

export const getChiefComplaints = (filters: DashboardFilters = {}, signal?: AbortSignal) =>
  apiGet<ChiefComplaint[]>('/chief-complaints', { query: filtersToQuery(filters), signal });

/* /chief-complaints/:id
 * -------------------------------------------------------------------------- */

/**
 * Clinical context shown above the indicator breakdown on the complaint drill.
 * Today TSG-sourced and hard-coded server-side; future home is a Databricks
 * reference table (PLA-251 / PLA-445 Tier 2). `null` when the API has no
 * content for this complaint — the UI omits the card in that case.
 */
export interface WhyThisMatters {
  title: string;
  /** Body paragraph; may include `**bold**` markdown for inline emphasis. */
  body: string;
  /** Short risk chips rendered as coral pills under the body. */
  risks: string[];
  /** Source attribution. `tsg` swaps in The Sullivan Group via the source catalog. */
  source: 'tsg' | 'ai-draft';
}

export interface ChiefComplaintDetail {
  id: string;
  name: string;
  /**
   * Distinct chart count for this complaint under the active filters.
   * Canonical "N" -- matches what the Lens overview shows for the same
   * complaint. Sourced from `COUNT(DISTINCT chart_id)` on the API.
   */
  chartCount: number;
  /**
   * Distinct patient count. Null until silver carries a patient identifier
   * (today only `VisitKey` is available, which is per-encounter, not
   * per-person). UI should render "--" when null.
   */
  patientCount: number | null;
  /**
   * Period-over-period comparison: the same composite (same complaint,
   * provider, hospital, formula) over the prior window of equal length.
   * Null when the window is open-ended (dateRange=all) or the prior window
   * has no scored charts. `periodDays` is the window length in days so the
   * chip can say "vs prior 90d" truthfully.
   */
  previousScore?: number | null;
  previousChartCount?: number | null;
  change?: number | null;
  periodDays?: number | null;
  trend?: TrendDirection;
  /**
   * Peer rank for this complaint (1 = best) over the ACTIVE filter window.
   * The API computes every provider's score from `gold_indicator_analytics`
   * with the same formula as the summary score and ranks this NPI within
   * that list, so rank follows whatever period is set. Null when no NPI is
   * available to anchor the lookup or when the anchor has no
   * denominator-eligible charts in the window.
   */
  rank: number | null;
  /** Providers scored for this complaint/window (cohort size). Null when rank is null. */
  peerCount: number | null;
  demographics: {
    gender: { male: number; female: number };
    ageBuckets: { label: string; count: number }[];
    arrivalMode: Record<string, number>;
  };
  whyThisMatters?: WhyThisMatters | null;
  documentationBreakdown: {
    rsq: { overall: number; subcategories: Record<string, number> };
    ffs: { overall: number; subcategories: Record<string, number> };
    vbc: { overall: number; subcategories: Record<string, number> };
  };
}

export const getChiefComplaint = (
  id: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<ChiefComplaintDetail>(`/chief-complaints/${encodeURIComponent(id)}`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /chief-complaints/:id/why-this-matters
 * --------------------------------------------------------------------------
 * Standalone fetch for the clinical context block. Useful when the UI wants
 * Why This Matters without paying for the heavier complaint detail query.
 * Returns `whyThisMatters: null` (plus a `reason`) when no content is
 * registered for the slug — the card simply does not render in that case.
 */

export interface WhyThisMattersResponse {
  id: string;
  whyThisMatters: WhyThisMatters | null;
  reason?: string;
}

export const getWhyThisMatters = (id: string, signal?: AbortSignal) =>
  apiGet<WhyThisMattersResponse>(`/chief-complaints/${encodeURIComponent(id)}/why-this-matters`, {
    signal,
  });

/* /chief-complaints/:id/indicators
 * -------------------------------------------------------------------------- */

export interface ComplaintIndicator {
  id: string;
  name: string;
  domain: string;
  scope: string;
  score: number;
  denom: number;
  missed: number;
  found: number;
  inconclusive: number;
  missed_pct: number;
  found_pct: number;
  inconclusive_pct: number;
  /** Target percent for this indicator. Org-set Vida goal; falls back to 40 when none is set. */
  goal?: number | null;
  /** Where `goal` came from: 'vida' = organization-set target, 'default' = fallback (no goal set yet). */
  goalSource?: 'vida' | 'default' | null;
  /**
   * Complaint-specific clinical description sourced from the Databricks
   * reference tooltip when available. Preferred over the generic bulk
   * /indicator-descriptions copy for the info tray.
   */
  description?: string | null;
  descriptionSource?: string | null;
  /** Clinical display order from reference data (lower = earlier). */
  ordinality?: number | null;
  /**
   * Qualifiers gate other indicators' denominators and never score
   * themselves; rendered flagged and excluded from group score aggregates.
   */
  isQualifier?: boolean;
  /**
   * True for Vitals rows: `score` is already inverted to compliance %
   * (100 - found rate) by the API because the raw found-rate measures
   * abnormality detection (low = good). found_pct/missed_pct stay raw.
   */
  inverted?: boolean;
}

export const getComplaintIndicators = (
  id: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<ComplaintIndicator[]>(`/chief-complaints/${encodeURIComponent(id)}/indicators`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /indicator-descriptions
 * --------------------------------------------------------------------------
 * Bulk indicator + section descriptions for the Lens info buttons. Fetched
 * once on Lens mount and cached; the content is static server-side. */

export type DescriptionSource = 'stub' | 'tsg' | 'paper-plane';

export interface DescriptionEntry {
  /** Normalized lookup key (lowercased, trimmed) matching the server. */
  key: string;
  name: string;
  description: string;
  source: DescriptionSource;
}

export interface IndicatorDescriptions {
  version: number;
  indicators: DescriptionEntry[];
  sections: DescriptionEntry[];
}

export const getIndicatorDescriptions = (signal?: AbortSignal) =>
  apiGet<IndicatorDescriptions>('/indicator-descriptions', { signal });

/* /subcategory-scores/:parentId
 * -------------------------------------------------------------------------- */

export interface SubcategoryScore {
  id: string;
  name: string;
  score: number;
  denom: number;
  found: number;
}

export const getSubcategoryScores = (
  parentId: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<SubcategoryScore[]>(`/subcategory-scores/${encodeURIComponent(parentId)}`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /indicator-performance-by-month
 * -------------------------------------------------------------------------- */

export interface IndicatorPerformancePeriod {
  period: string;
  periodLabel: string;
}

export interface IndicatorPerformanceRow {
  id: string;
  name: string;
  shortName: string;
  category: string;
  domain: string;
  scores: (number | null)[];
  currentScore: number | null;
  trend: 'up' | 'down' | 'neutral';
}

export interface IndicatorPerformanceResponse {
  periods: IndicatorPerformancePeriod[];
  indicators: IndicatorPerformanceRow[];
}

export const getIndicatorPerformanceByMonth = (
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<IndicatorPerformanceResponse>('/indicator-performance-by-month', {
    query: filtersToQuery(filters),
    signal,
  });

/* /overall-stats
 * -------------------------------------------------------------------------- */

export interface OverallStats {
  /** Distinct chart_id seen by the pipeline in the window (broad encounter count). */
  total_visits: number;
  /** Distinct chart_id that contributed at least one valid measurement (narrow analyzed set). */
  total_charts: number;
  totalRecords: number;
  completionRate: number;
  overall_documentation_score: number;
  /** Total number of indicator validations performed. */
  indicators_evaluated?: number;
  /** Total indicators successfully captured. */
  indicators_found?: number;
  denom: number;
  found: number;
  /** Same-length prior window comparisons (null when window is open-ended). */
  previousVisits?: number | null;
  previousCharts?: number | null;
  previousScore?: number | null;
}

export const getOverallStats = (filters: DashboardFilters = {}, signal?: AbortSignal) =>
  apiGet<OverallStats>('/overall-stats', { query: filtersToQuery(filters), signal });

/* /recent-activity
 * -------------------------------------------------------------------------- */

/**
 * Trend-based activity item. Replaces the old captured/missed indicator
 * events with priority-ranked alerts, stats, and trends sourced from
 * v_gold_provider_period_trends + v_gold_provider_indicator_period_trends.
 *
 * `type` drives the icon and tone; `category` is the specific rule that
 * fired (kept stable so analytics can dimension on it); `message` is the
 * pre-rendered user-facing string from the API.
 */
export type RecentActivityType = 'alert' | 'stat' | 'trend';

export type RecentActivityCategory =
  | 'overall_decline'
  | 'volatility'
  | 'indicator_decline'
  | 'top_rank'
  | 'perfect_score'
  | 'overall_improve'
  | 'best_indicator'
  | 'volume_milestone';

export interface RecentActivityItem {
  id: string;
  /** Position within the feed (1 = most important). */
  rank: number;
  /** Lower = higher priority. 1-2 are alerts, 3-5 are stats/trends. */
  priority: number;
  type: RecentActivityType;
  category: RecentActivityCategory | string;
  /** Emoji for the icon slot (e.g. "⚠️", "🏆"). Already chosen for the item type. */
  icon: string;
  /** Headline, one short clause (e.g. "Risk Indicator Scores declined"). */
  title: string;
  /** Detail line, plain narrative (e.g. "From 60% to 53% in the last 30 days"). */
  message: string;
  provider: string | null;
  providerNpi: string;
}

export interface RecentActivityResponse {
  data: RecentActivityItem[];
  windowDays: 30 | 60 | 90 | 180;
}

export const getRecentActivity = (
  filters: DashboardFilters & { limit?: number; windowDays?: 30 | 60 | 90 | 180 } = {},
  signal?: AbortSignal,
) =>
  apiGet<RecentActivityItem[]>('/recent-activity', {
    query: {
      ...filtersToQuery(filters),
      limit: filters.limit,
      windowDays: filters.windowDays,
    },
    signal,
  });

/* /home-highlights
 * -------------------------------------------------------------------------- */

export interface HighlightIndicator {
  id: string;
  name: string;
  shortName: string;
  score: number;
  previousScore: number | null;
  change: number;
  totalCharts: number;
}

export interface HighlightProvider {
  id: string;
  name: string;
  score: number;
  previousScore: number | null;
  change: number;
  totalCharts: number;
}

export interface HighlightComplaint {
  id: string;
  name: string;
  count: number;
  previousCount: number | null;
  change: number;
  score: number | null;
}

export interface HomeHighlights {
  period: { start: string | null; end: string | null };
  previousPeriod: { start: string; end: string } | null;
  summary: string | null;
  indicators: { improving: HighlightIndicator[]; declining: HighlightIndicator[] };
  providers: { improving: HighlightProvider[]; declining: HighlightProvider[] };
  complaints: { rising: HighlightComplaint[]; falling: HighlightComplaint[] };
}

export const getHomeHighlights = (
  filters: DashboardFilters & { limit?: number; minDenom?: number } = {},
  signal?: AbortSignal,
) =>
  apiGet<HomeHighlights>('/home-highlights', {
    query: { ...filtersToQuery(filters), limit: filters.limit, minDenom: filters.minDenom },
    signal,
  });

/* /health
 * -------------------------------------------------------------------------- */

export interface HealthCheck {
  status: 'ok' | 'error';
  db?: boolean;
  error?: string;
}

export const getHealth = (signal?: AbortSignal) => apiGet<HealthCheck>('/health', { signal });

/* /chief-complaints/:id/trends
 * -------------------------------------------------------------------------- */

/**
 * Rolling-window trends for a complaint, sourced from
 * `v_gold_provider_period_trends`. Returns one block per window
 * (30 / 60 / 90 / 180 days) plus a flat headline (default 90d).
 *
 * Aggregated at the tenant level: `tenantScore` is the tenant-wide rolling
 * RSQ for the window; `trendDelta` / `scoreMin` / `scoreMax` / `earlyScore` /
 * `lateScore` / `volatility` are AVG across providers, `totalCharts` is SUM.
 */
export type TrendDirection = 'up' | 'down' | 'flat';

export interface ComplaintTrendWindow {
  windowDays: number;
  asOfDate: string | null;
  tenantScore: number | null;
  trendDelta: number | null;
  trendDirection: TrendDirection;
  scoreMin: number | null;
  scoreMax: number | null;
  earlyScore: number | null;
  lateScore: number | null;
  totalCharts: number | null;
  weeksActive: number | null;
  volatility: number | null;
  /** Total tenant peers ranked for this window. */
  tenantPeerCount: number | null;
  /** Provider's current rolling rank within tenantPeerCount (1 = best). */
  tenantRank: number | null;
}

/**
 * One bar in the per-complaint monthly trend chart. The Lens > Complaint >
 * Trends panel renders four of these (current month plus the three prior
 * calendar months) so the user sees how this complaint's score is moving
 * month over month, matching the mock at Mock/Kai-Mock-V2.html.
 */
export interface ComplaintMonthlyPoint {
  /** 'YYYY-MM' key, useful for stable map/sort logic. */
  ym: string;
  /** Three-letter month label rendered under the bar (Jan / Feb / ...). */
  month: string;
  year: number;
  /** Provider's RSQ for the month. Null when no chart had include_in_denom = true. */
  score: number | null;
  /** Chart-evaluation rows the score was computed from (denom, not chart_id). */
  charts: number;
}

export interface ComplaintTrends {
  id: string;
  available: boolean;
  reason?: string;
  headlineWindowDays: number;
  asOfDate: string | null;
  /**
   * Volume-weighted AVERAGE score across the window the bar chart shows
   * (the last 4 calendar months by default). Rendered under the "Average"
   * label; every other stat on the Trends tab derives from the same window.
   */
  current: number | null;
  /** Explicit alias for `current` (the window average). */
  average?: number | null;
  /** late - early over the displayed window (positive = improving). */
  trendDelta: number | null;
  trendDirection: TrendDirection;
  scoreMin: number | null;
  scoreMax: number | null;
  earlyScore: number | null;
  lateScore: number | null;
  totalCharts: number | null;
  weeksActive: number | null;
  volatility: number | null;
  tenantPeerCount: number | null;
  tenantRank: number | null;
  windows: ComplaintTrendWindow[];
  /**
   * Monthly bars covering the active filter window (or last 4 months
   * when no filter is set). Each bar is a full calendar month;
   * `score` is SUM(numerator) / SUM(denominator) from
   * gold_provider_indicator_period_scores; `charts` is COUNT(DISTINCT
   * chart_id) from gold_indicator_chart_scores so the bar count is
   * true distinct charts, not the inflated per-indicator-week sum.
   */
  monthly: ComplaintMonthlyPoint[];
  /**
   * late_half_score - early_half_score derived from the monthly array
   * (first half vs last half). Matches the bar chart visually -- the
   * view's 90-day rolling early/late is exposed per-window via
   * `windows[].earlyScore` / `lateScore` if needed.
   */
  monthlyDelta: number | null;
  /**
   * Human-readable label for the trend window (e.g. "Mar – Jun 2026"
   * or "May – Jun 2026" for shorter filters). Reflects the same
   * window the bars cover.
   */
  windowLabel: string | null;
}

export const getComplaintTrends = (
  id: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<ComplaintTrends>(`/chief-complaints/${encodeURIComponent(id)}/trends`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /chief-complaints/:id/plan
 * --------------------------------------------------------------------------
 * Complaint-scoped "View My Plan". Mirrors the provider-wide Plan contract
 * (PlanResult) with an extra `complaint` header block. Returns the
 * `available: false` processing state when no provider NPI is in scope or
 * the complaint has no chart volume in the window. */

/** Complaint header block returned alongside the complaint-scoped plan. */
export interface ComplaintPlanHeader {
  id: string;
  name: string;
  /** Compliance-adjusted overall score for this complaint (%). */
  score: number;
  chartCount: number;
  rank: number | null;
  peerCount: number | null;
  trendDelta: number | null;
}

/** Full plan payload for one chief complaint. */
export type ComplaintPlanResponse = (Plan & { complaint: ComplaintPlanHeader }) | PlanUnavailable;

export const getComplaintPlan = (
  id: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<ComplaintPlanResponse>(`/chief-complaints/${encodeURIComponent(id)}/plan`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /plan  ("View My Plan", PLA-253)
 * --------------------------------------------------------------------------
 * Mirrors the Kai-API PlanService.PlanResult contract served by GET
 * /api/v1/app/plan. The endpoint returns either the full plan, or an
 * `available: false` processing state when no provider NPI is supplied (or no
 * data exists yet). */

export type PlanBand = 'excellent' | 'good' | 'attention';

export interface PlanDomainView {
  key: string;
  label: string;
  score: number | null;
  rank: number | null;
  total: number | null;
  band: PlanBand | null;
  coverage: 'available' | 'processing';
}

/** Structured card content; `note` is the flattened fallback sentence. */
export interface PlanEvidence {
  /** Eligible evaluations behind the score. */
  denom: number;
  /** Distinct chief complaints the indicator was evaluated on. */
  complaintCount: number;
  /** Complaints where the indicator is strongest (strength) / weakest (gap). */
  where: string[];
  /** One-sentence clinical rationale, when known. */
  why: string | null;
  /** Abnormality-detection (Vital Signs) indicator shown on the compliance scale. */
  inverted: boolean;
}

export interface PlanStrength {
  id: string;
  name: string;
  domain: string;
  domainLabel: string;
  /** Chart section for indicator-level entries (History, Physical Exam, ...). */
  section?: string | null;
  score: number;
  rank: number | null;
  total: number | null;
  band: PlanBand;
  note: string | null;
  evidence?: PlanEvidence | null;
}

export interface PlanOpportunity {
  id: string;
  name: string;
  domain: string;
  domainLabel: string;
  /** Chart section for indicator-level entries (History, Physical Exam, ...). */
  section?: string | null;
  type: string;
  score: number;
  goal: number;
  rank: number | null;
  total: number | null;
  /** Estimated composite lift if brought to goal, e.g. "+9%". */
  impact: string;
  impactPts: number;
  impactDomain: string;
  moduleId: string;
  sessions: number;
  completed: boolean;
  note: string | null;
  evidence?: PlanEvidence | null;
}

export interface Plan {
  generatedAt: string;
  /** Drives the "Powered by AI" badge. */
  source: 'rule-based' | 'ai-assisted' | 'owl';
  modelVersion: string;
  period: { code: string; label: string };
  overall: { completed: number; total: number };
  performance: {
    summary: string;
    domains: PlanDomainView[];
  };
  strengths: PlanStrength[];
  opportunities: PlanOpportunity[];
  /** Set by the route when served from the nightly job vs generated on demand. */
  cached?: boolean;
}

/** Processing/empty state returned when the plan can't be generated yet. */
export interface PlanUnavailable {
  available: false;
  reason: string;
  period: { code: string; label: string };
}

export type PlanResponse = Plan | PlanUnavailable;

/** Narrow the union: the unavailable state carries `available: false`. */
export const isPlanAvailable = (p: PlanResponse): p is Plan =>
  (p as PlanUnavailable).available !== false;

export const getPlan = (filters: DashboardFilters = {}, signal?: AbortSignal) =>
  apiGet<PlanResponse>('/plan', { query: filtersToQuery(filters), signal });

/* /domains/:domain/subdomain-trend
 * -------------------------------------------------------------------------- */

/**
 * One sub-domain row in the Home RSQ drill-down modal. `score` is the
 * section's RSQ over the whole window (the same SUM/SUM formula as the modal
 * header and the Home tile, so the rows are a breakdown of the header).
 * `history` is the monthly score per `monthlyKeys` bucket (null when no
 * eligible charts) and `delta` is last month minus first month with data.
 * `inverted` flags rows whose raw rate measures abnormality (Vital Signs);
 * the score is already on the compliance scale.
 */
export interface DomainSubdomain {
  id: string;
  name: string;
  score: number | null;
  history: Array<number | null>;
  /**
   * Period-over-period movement in points: this window's section score minus
   * the equal prior window's (same definition as the RSQ tile and Lens row
   * chips). Null when either window has no denominator.
   */
  delta?: number | null;
  /** The prior window's section score the delta was measured against. */
  previousScore?: number | null;
  charts: number | null;
  inverted: boolean;
}

export interface DomainSubdomainTrend {
  id: string;
  label: string;
  score: number | null;
  rank: number | null;
  cohortSize: number | null;
  chartCount: number | null;
  subs: DomainSubdomain[];
  monthlyKeys: Array<{ ym: string; month: string; year: number }>;
  windowLabel: string | null;
  reason?: string;
}

export const getDomainSubdomainTrend = (
  domain: string,
  filters: DashboardFilters = {},
  signal?: AbortSignal,
) =>
  apiGet<DomainSubdomainTrend>(`/domains/${encodeURIComponent(domain)}/subdomain-trend`, {
    query: filtersToQuery(filters),
    signal,
  });

/* /me
 * ------------------------------------------------------------------ */

/** Identity + provider scope for the signed-in user (GET /me). */
export interface MyIdentity {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  /** 10-digit NPI resolved from the Entra extn.npi claim / identity table. */
  npi: string | null;
  /** False when no NPI could be resolved — data cannot be personalized. */
  providerScoped: boolean;
}

export const getMyIdentity = (signal?: AbortSignal) => apiGet<MyIdentity>('/me', { signal });
